import React, { useState } from 'react';
import { useWorkspace } from '../../../context/WorkspaceContext';
import { Card } from '../../../design-system/Card';
import { Button } from '../../../design-system/Button';
import { Badge } from '../../../design-system/Badge';
import { Modal } from '../../../design-system/Modal';
import { Input, Textarea } from '../../../design-system/Input';
import {
  Calendar,
  Clock,
  Video,
  Plus,
  Sparkles,
  Check,
  CheckCircle2,
  Bookmark
} from 'lucide-react';

export const MeetingsView = () => {
  const { addTask, setSelectedTask, allTasks = [] } = useWorkspace();
  const [activeMeetingId, setActiveMeetingId] = useState('meet_1');
  const [isNewMeetingModalOpen, setIsNewMeetingModalOpen] = useState(false);

  // New Meeting Form
  const [newTitle, setNewTitle] = useState('');
  const [newDate, setNewDate] = useState('2026-08-25');
  const [newTime, setNewTime] = useState('10:00 AM');
  const [newDuration, setNewDuration] = useState('45 mins');
  const [newAgenda, setNewAgenda] = useState('');

  const [meetings, setMeetings] = useState([
    {
      id: 'meet_1',
      title: 'Forma Platform V1 Sprint Planning & Architecture Review',
      date: '2026-08-24',
      time: '10:00 AM - 10:45 AM',
      duration: '45 mins',
      location: 'Google Meet (meet.forma.build/arch)',
      organizer: 'Rohith Prince',
      attendees: [
        { name: 'Rohith Prince', avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&auto=format&fit=crop&q=80', status: 'Accepted' },
        { name: 'Sarah Connor', avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&auto=format&fit=crop&q=80', status: 'Accepted' },
        { name: 'Marcus Vance', avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&auto=format&fit=crop&q=80', status: 'Accepted' }
      ],
      notes: `### Agenda Topics\n1. Review Neomorphic Dual-Shadow calibration on dark surfaces.\n2. Validate Node.js 22 modular architecture on Zoho Catalyst AppSail.\n3. Finalize Sprint 1 deliverables timeline.`,
      decisions: [
        {
          id: 'dec_1',
          decision: 'Standardize on #111417 base charcoal for dark mode elevation modeling.',
          rationale: 'Provides optimal WCAG contrast and smooth recessed shadows without eye fatigue.'
        },
        {
          id: 'dec_2',
          decision: 'Use zero lock-in methodology packs with dynamic modular navigation.',
          rationale: 'Allows teams to switch disciplines without modifying or risking task data.'
        }
      ],
      actionItems: [
        { id: 'act_1', title: 'Verify contrast on Safari mobile devices', assignee: 'Rohith Prince', convertedTaskId: 'FOR-101', done: false },
        { id: 'act_2', title: 'Draft visual asset guidelines for game dev studio module', assignee: 'Sarah Connor', convertedTaskId: null, done: false }
      ]
    },
    {
      id: 'meet_2',
      title: 'Weekly Cross-Team Sync & Design Alignment',
      date: '2026-08-21',
      time: '02:00 PM - 02:30 PM',
      duration: '30 mins',
      location: 'Conference Room Alpha',
      organizer: 'Sarah Connor',
      attendees: [
        { name: 'Rohith Prince', avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&auto=format&fit=crop&q=80', status: 'Accepted' },
        { name: 'Sarah Connor', avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&auto=format&fit=crop&q=80', status: 'Accepted' }
      ],
      notes: `Discussed user feedback on sidebar collapsibility and template onboarding.`,
      decisions: [
        {
          id: 'dec_3',
          decision: 'Implement collapsible sidebar with icon fallback.',
          rationale: 'Gives users maximum screen canvas for dense Kanban and spreadsheet table views.'
        }
      ],
      actionItems: [
        { id: 'act_3', title: 'Add collapse state persistence to localStorage', assignee: 'Rohith Prince', convertedTaskId: 'FOR-102', done: true }
      ]
    }
  ]);

  const activeMeeting = meetings.find((m) => m.id === activeMeetingId) || meetings[0];

  // Convert an action item into a real project task
  const handleConvertActionItem = (actionItemId) => {
    const item = activeMeeting.actionItems.find((a) => a.id === actionItemId);
    if (!item || item.convertedTaskId) return;

    const newTask = {
      title: item.title,
      description: `Action item originated from meeting: "${activeMeeting.title}" on ${activeMeeting.date}`,
      status: 'todo',
      priority: 'medium',
      taskType: 'task',
      assignee: { name: item.assignee, avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&auto=format&fit=crop&q=80' },
      estimate: '3 pts'
    };

    const created = addTask(newTask);
    const taskId = created?.id || `FOR-${Math.floor(100 + Math.random() * 900)}`;

    setMeetings((prev) =>
      prev.map((m) =>
        m.id === activeMeeting.id
          ? {
              ...m,
              actionItems: m.actionItems.map((a) =>
                a.id === actionItemId ? { ...a, convertedTaskId: taskId } : a
              )
            }
          : m
      )
    );
  };

  const handleToggleActionDone = (actionItemId) => {
    setMeetings((prev) =>
      prev.map((m) =>
        m.id === activeMeeting.id
          ? {
              ...m,
              actionItems: m.actionItems.map((a) =>
                a.id === actionItemId ? { ...a, done: !a.done } : a
              )
            }
          : m
      )
    );
  };

  const handleCreateMeetingSubmit = (e) => {
    e?.preventDefault();
    if (!newTitle.trim()) return;

    const newM = {
      id: 'meet_' + Date.now(),
      title: newTitle.trim(),
      date: newDate,
      time: `${newTime} (${newDuration})`,
      duration: newDuration,
      location: 'Google Meet Video Call',
      organizer: 'Rohith Prince',
      attendees: [
        { name: 'Rohith Prince', avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&auto=format&fit=crop&q=80', status: 'Accepted' },
        { name: 'Sarah Connor', avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&auto=format&fit=crop&q=80', status: 'Accepted' }
      ],
      notes: newAgenda ? `### Agenda\n${newAgenda}` : '### Agenda Topics\n1. Project review and alignment.',
      decisions: [],
      actionItems: []
    };

    setMeetings([newM, ...meetings]);
    setActiveMeetingId(newM.id);
    setNewTitle('');
    setNewAgenda('');
    setIsNewMeetingModalOpen(false);
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '300px 1fr',
        gap: '20px',
        minHeight: '560px'
      }}
      className="animate-fade-in"
    >
      {/* Meetings List Sidebar */}
      <div
        style={{
          backgroundColor: 'var(--surface-recessed)',
          boxShadow: 'var(--shadow-recessed-sm)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-card)',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '800' }}>
            <Calendar size={16} color="var(--accent-teal)" />
            <span>Meetings ({meetings.length})</span>
          </div>
          <Button
            variant="primary"
            size="sm"
            icon={<Plus size={14} />}
            onClick={() => setIsNewMeetingModalOpen(true)}
          >
            Schedule
          </Button>
        </div>

        {/* Meeting Cards List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', overflowY: 'auto', maxHeight: '480px' }}>
          {meetings.map((m) => {
            const isSelected = m.id === activeMeetingId;
            return (
              <div
                key={m.id}
                onClick={() => setActiveMeetingId(m.id)}
                style={{
                  padding: '12px 14px',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: isSelected ? 'var(--surface-card)' : 'transparent',
                  boxShadow: isSelected ? 'var(--shadow-raised-sm)' : 'none',
                  border: isSelected ? '1px solid var(--border-subtle)' : '1px solid transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  transition: 'all 0.12s'
                }}
              >
                <div style={{ fontSize: '13px', fontWeight: '700', color: isSelected ? 'var(--accent-teal)' : 'var(--text-primary)', lineHeight: '1.3' }}>
                  {m.title}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', color: 'var(--text-muted)' }}>
                  <span>{m.date}</span>
                  <span>•</span>
                  <span>{m.duration}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Meeting Details View */}
      {activeMeeting ? (
        <Card padding="28px">
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '20px', borderBottom: '1px solid var(--border-divider)', paddingBottom: '16px' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <Badge variant="teal" size="xs">Meeting Sync</Badge>
                <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Organized by {activeMeeting.organizer}</span>
              </div>
              <h1 style={{ fontSize: '20px', fontWeight: '800' }}>{activeMeeting.title}</h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginTop: '6px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Calendar size={13} color="var(--accent-teal)" /> {activeMeeting.date}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Clock size={13} /> {activeMeeting.time}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Video size={13} color="var(--color-info)" /> {activeMeeting.location}
                </span>
              </div>
            </div>

            {/* Attendees */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {activeMeeting.attendees.map((att) => (
                <img
                  key={att.name}
                  src={att.avatar}
                  alt={att.name}
                  title={`${att.name} (${att.status})`}
                  style={{ width: '32px', height: '32px', borderRadius: '999px', objectFit: 'cover', border: '2px solid var(--surface-card)' }}
                />
              ))}
            </div>
          </div>

          {/* Agenda & Notes */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '800', marginBottom: '8px', color: 'var(--text-primary)' }}>
              Agenda & Discussion Notes
            </h3>
            <div style={{ padding: '14px 18px', backgroundColor: 'var(--surface-recessed)', borderRadius: 'var(--radius-md)', fontSize: '13px', lineHeight: '1.6', whiteSpace: 'pre-wrap', color: 'var(--text-secondary)' }}>
              {activeMeeting.notes}
            </div>
          </div>

          {/* Key Decisions Log */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
              <Bookmark size={16} color="var(--color-purple)" />
              <h3 style={{ fontSize: '14px', fontWeight: '800', color: 'var(--text-primary)' }}>
                Recorded Decisions ({activeMeeting.decisions.length})
              </h3>
            </div>

            {activeMeeting.decisions.length === 0 ? (
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                No formalized decisions logged for this sync.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {activeMeeting.decisions.map((dec) => (
                  <div
                    key={dec.id}
                    style={{
                      padding: '12px 16px',
                      backgroundColor: 'rgba(168, 85, 247, 0.08)',
                      border: '1px solid rgba(168, 85, 247, 0.3)',
                      borderRadius: 'var(--radius-md)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px'
                    }}
                  >
                    <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>
                      ✓ {dec.decision}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                      <strong>Rationale:</strong> {dec.rationale}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Action Items Tracker & 1-Click Task Converter */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <CheckCircle2 size={16} color="var(--color-success)" />
                <h3 style={{ fontSize: '14px', fontWeight: '800', color: 'var(--text-primary)' }}>
                  Action Items & Task Traceability ({activeMeeting.actionItems.length})
                </h3>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {activeMeeting.actionItems.map((item) => {
                const isConverted = !!item.convertedTaskId;
                const convertedTask = isConverted ? allTasks.find((t) => t.id === item.convertedTaskId) : null;

                return (
                  <div
                    key={item.id}
                    style={{
                      padding: '12px 16px',
                      backgroundColor: 'var(--surface-recessed)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--border-subtle)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '12px'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                      <input
                        type="checkbox"
                        checked={item.done}
                        onChange={() => handleToggleActionDone(item.id)}
                        style={{ accentColor: 'var(--accent-teal)', cursor: 'pointer' }}
                      />
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: item.done ? 'var(--text-muted)' : 'var(--text-primary)', textDecoration: item.done ? 'line-through' : 'none' }}>
                          {item.title}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                          Assignee: {item.assignee}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {isConverted ? (
                        <button
                          onClick={() => convertedTask && setSelectedTask(convertedTask)}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: 'rgba(35, 190, 179, 0.12)',
                            border: '1px solid var(--accent-teal)',
                            borderRadius: 'var(--radius-sm)',
                            color: 'var(--accent-teal)',
                            fontSize: '11px',
                            fontWeight: '700',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <Check size={12} /> Task: {item.convertedTaskId}
                        </button>
                      ) : (
                        <Button
                          variant="secondary"
                          size="sm"
                          icon={<Sparkles size={13} />}
                          onClick={() => handleConvertActionItem(item.id)}
                        >
                          Convert to Task
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      ) : (
        <Card padding="32px">
          <div style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
            Select a meeting to review agenda, decisions, and action items.
          </div>
        </Card>
      )}

      {/* SCHEDULE MEETING MODAL */}
      <Modal
        isOpen={isNewMeetingModalOpen}
        onClose={() => setIsNewMeetingModalOpen(false)}
        title="Schedule Project Meeting"
        subtitle="Set date, invite team participants, and draft agenda"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsNewMeetingModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCreateMeetingSubmit} icon={<Calendar size={14} />}>
              Schedule Sync
            </Button>
          </>
        }
      >
        <form onSubmit={handleCreateMeetingSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <Input
            label="Meeting Title *"
            placeholder="e.g., Sprint 2 Grooming & Planning"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            required
            autoFocus
          />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <Input
              label="Date *"
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              required
            />
            <Input
              label="Start Time"
              value={newTime}
              onChange={(e) => setNewTime(e.target.value)}
            />
          </div>

          <Input
            label="Duration"
            value={newDuration}
            onChange={(e) => setNewDuration(e.target.value)}
            placeholder="30 mins, 45 mins, 1 hour..."
          />

          <Textarea
            label="Discussion Agenda"
            placeholder="Key topics, questions, and goals for this sync..."
            value={newAgenda}
            onChange={(e) => setNewAgenda(e.target.value)}
            rows={3}
          />
        </form>
      </Modal>
    </div>
  );
};
